<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SelectOption } from 'naive-ui';
import type { ContractGenerationTemplateFieldInDb } from '@/api';
import type { FieldMetadataDraft } from '@/composables/contractGeneration/useTemplateFieldDraft';
import type { FieldTreeRow } from '@/utils/contractGeneration/templateFieldTree';
import { lastPathSegment } from '@/utils/contractGeneration/templateFieldLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaConfirmCard from '@/components/design/composite/GaiaConfirmCard.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import { gaiaFonts } from '@/components/design/themes/tokens';

/**
 * What an author writes about the selected field — label, description,
 * guidance — under the inspector header the page draws above it. An orphaned
 * row shows its writing instead and offers to move it onto the field that
 * replaced it.
 */
interface Props {
  row: FieldTreeRow | null;
  orphan: ContractGenerationTemplateFieldInDb | null;
  draft: FieldMetadataDraft | null;
  /** Live fields an orphan's notes can be moved onto, in tree order. */
  adoptTargets: FieldTreeRow[];
  resolveLabel: (row: FieldTreeRow) => string;
  hasNotes: (path: string) => boolean;
  /** Label of the field this orphan's notes were already moved onto. */
  adoptedTo: string | null;
  disabled: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  update: [patch: Partial<FieldMetadataDraft>];
  adopt: [targetPath: string];
  /** Remove the selected orphan's row for good. */
  delete: [];
}>();

const backendLimits = { label: 1000, prose: 10_000 } as const;

// --- orphan re-adopt ---------------------------------------------------------

const adoptTarget = ref<string | null>(null);
const confirmingReplace = ref<boolean>(false);
const confirmingDelete = ref<boolean>(false);

const adoptOptions = computed<SelectOption[]>(() =>
  props.adoptTargets.map(row => ({
    label: `${' '.repeat(row.depth * 2)}${props.resolveLabel(row)} · ${row.path}`,
    value: row.path,
  }))
);

// Best guess for where the notes belong: a live field with the same tag name
// at a different place, which is what a renamed parent produces.
watch(
  () => props.orphan?.path ?? null,
  orphanPath => {
    confirmingReplace.value = false;
    confirmingDelete.value = false;
    if (!orphanPath) {
      adoptTarget.value = null;
      return;
    }
    const segment = lastPathSegment(orphanPath);
    adoptTarget.value =
      props.adoptTargets.find(row => lastPathSegment(row.path) === segment)?.path ?? null;
  },
  { immediate: true }
);

const adoptTargetRow = computed<FieldTreeRow | null>(() =>
  adoptTarget.value
    ? (props.adoptTargets.find(row => row.path === adoptTarget.value) ?? null)
    : null
);

function requestAdopt(): void {
  if (!adoptTarget.value) return;
  if (props.hasNotes(adoptTarget.value)) {
    confirmingReplace.value = true;
    return;
  }
  emit('adopt', adoptTarget.value);
}

function confirmAdopt(): void {
  confirmingReplace.value = false;
  if (adoptTarget.value) emit('adopt', adoptTarget.value);
}

const orphanColumns = computed<{ label: string; text: string }[]>(() => {
  const orphan = props.orphan;
  if (!orphan) return [];
  return [
    { label: 'Label', text: orphan.label ?? '' },
    { label: 'Description', text: orphan.description ?? '' },
    { label: 'Guidance', text: orphan.guidance ?? '' },
  ].filter(column => column.text.trim().length > 0);
});

// --- chrome ------------------------------------------------------------------
const monoFont = gaiaFonts.mono;
</script>

<template>
  <div :style="{ padding: '18px 22px 28px' }">
    <!-- An orphan: its writing, kept, and where it could go. -->
    <n-flex v-if="orphan" vertical :size="16">
      <gaia-banner type="warning" title="This field is no longer in the document">
        Its guidance was kept. Move it onto the field that replaced it, or leave it here in case the
        tag comes back.
      </gaia-banner>

      <gaia-sub-panel label="Field">
        <gaia-text size="dense" weight="semibold" tone="ink" :style="{ fontFamily: monoFont }">
          {{ orphan.path }}
        </gaia-text>
      </gaia-sub-panel>

      <gaia-sub-panel v-for="column in orphanColumns" :key="column.label" :label="column.label">
        <gaia-text size="dense" tone="text" :style="{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }">
          {{ column.text }}
        </gaia-text>
      </gaia-sub-panel>

      <gaia-text v-if="adoptedTo" size="dense" tone="tertiary" :style="{ lineHeight: 1.5 }">
        Moved to “{{ adoptedTo }}” — save to keep it, then remove this entry.
      </gaia-text>

      <template v-else>
        <gaia-form-field label="Move guidance to">
          <gaia-select
            v-model:value="adoptTarget"
            :options="adoptOptions"
            filterable
            placeholder="Choose the field that replaced it"
            :disabled="disabled"
            style="width: 100%"
          />
        </gaia-form-field>
        <gaia-confirm-card
          v-if="confirmingReplace && adoptTargetRow"
          confirm-label="Replace guidance"
          @confirm="confirmAdopt"
          @cancel="confirmingReplace = false"
        >
          “{{ resolveLabel(adoptTargetRow) }}” already has guidance. Moving this here replaces it.
        </gaia-confirm-card>
        <n-flex v-else>
          <gaia-button
            type="primary"
            size="small"
            :disabled="!adoptTarget || disabled"
            @click="requestAdopt"
          >
            Move guidance here
          </gaia-button>
        </n-flex>
      </template>

      <gaia-confirm-card
        v-if="confirmingDelete"
        confirm-label="Remove entry"
        @confirm="emit('delete')"
        @cancel="confirmingDelete = false"
      >
        This removes the entry and the guidance written on it for good.
      </gaia-confirm-card>
      <n-flex v-else>
        <gaia-button size="small" quaternary :disabled="disabled" @click="confirmingDelete = true">
          Remove this entry
        </gaia-button>
      </n-flex>
    </n-flex>

    <!-- A live field: the header above names it; this is what the author writes about it. -->
    <n-flex v-else-if="row && draft" vertical :size="18">
      <n-flex vertical :size="2">
        <gaia-text size="body" weight="semibold" tone="ink">About this field</gaia-text>
        <gaia-text size="meta" tone="subtle">
          What the people filling in this template see next to the field.
        </gaia-text>
      </n-flex>

      <gaia-form-field label="Label" :hint="`Shown as “${row.fallbackLabel}” if left blank`">
        <gaia-input
          :value="draft.label ?? ''"
          :placeholder="row.fallbackLabel"
          :maxlength="backendLimits.label"
          :disabled="disabled"
          clearable
          style="width: 100%"
          @update:value="(value: string) => emit('update', { label: value })"
        />
      </gaia-form-field>

      <gaia-form-field label="Description" hint="What this field is">
        <gaia-textarea
          :value="draft.description ?? ''"
          placeholder="What goes here and where it appears in the document"
          :maxlength="backendLimits.prose"
          :autosize="{ minRows: 2, maxRows: 8 }"
          :disabled="disabled"
          style="width: 100%"
          @update:value="(value: string) => emit('update', { description: value })"
        />
      </gaia-form-field>

      <gaia-form-field
        label="Guidance"
        hint="How to choose a value — shown behind a ? beside the field"
      >
        <gaia-textarea
          :value="draft.guidance ?? ''"
          placeholder="What a good value looks like, and what to check before filling it in"
          :maxlength="backendLimits.prose"
          :autosize="{ minRows: 2, maxRows: 8 }"
          :disabled="disabled"
          style="width: 100%"
          @update:value="(value: string) => emit('update', { guidance: value })"
        />
      </gaia-form-field>
    </n-flex>

    <n-flex v-else justify="center" :style="{ padding: '48px 16px' }">
      <gaia-empty-state title="Pick a field">
        Choose a field on the left to name it and write guidance for the people who fill it in.
      </gaia-empty-state>
    </n-flex>
  </div>
</template>
